import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useI18nValues as useAssetValues } from '@/entities/assets';
import { useFileEncryptionUpload } from '@/entities/file/hooks/useFileEncryptionUpload';
import { useInboxEmailList } from '@/entities/inbox/hooks/useInboxEmailList';
import { useI18nValues as usePrivateDocValues } from '@/entities/privateDocuments/hooks/useI18nValues';
import { getCountryName } from '@/shared/dictionaries/countries';
import { getLanguageName } from '@/shared/dictionaries/languages';
import {
  LegalizationTypeList,
  NAME_KEY_BY_ASSET_CATEGORY,
  PersonalIdentifierDocumentDto,
  useSJDatabase,
} from '@/shared/lib/sj-orm';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';
import {
  EntityPdfData,
  EntityPdfField,
  EntityPdfSection,
} from '@/shared/pdf-templates/EntityPDF/types';
import { dates } from '@/shared/utils/dates';

import { PersonalIdentifierFieldValue } from '../components/PersonalIdentifierFieldValue';
import { getPersonalIdentifierForms } from '../helpers/get-forms';

import { useActions } from './useActions';
import { useAttributeGroups } from './useAttributeGroups';

const EMPTY_TEXT = '-';
const MAX_SECTIONS = 2;

export const usePersonalIdPdfData = () => {
  const { t } = useTranslation('documents');
  const { t: tf } = useTranslation('forms_personal_ids');
  const assetValues = useAssetValues();
  const privateDocValues = usePrivateDocValues();
  const { getGroups } = useAttributeGroups();
  const { findFileMetaByPath } = useFileEncryptionUpload();
  const inboxList = useInboxEmailList(true);
  const sjDb = useSJDatabase();
  const { getLinkedAssets, getLinkedPrivateDocuments } = useActions();

  return useMemo(() => {
    const getEntityData = (personalId: PersonalIdentifierDocumentDto): EntityPdfData => {
      const formFields = getPersonalIdentifierForms(personalId.personalIdentifierType)?.slice(
        0,
        MAX_SECTIONS,
      );
      const groups = getGroups(personalId.personalIdentifierType);

      if (!formFields) return { sections: [] };

      //#region General & Details
      const sections: EntityPdfSection[] = formFields.map((sectionItems, index) => {
        const fields: EntityPdfField[] = Object.entries(sectionItems).map(
          ([fieldKey, fieldValue]) => {
            const label = t(fieldValue.label);

            return {
              label,
              value: (
                <PersonalIdentifierFieldValue
                  personalIdentifier={personalId}
                  fieldKey={fieldKey}
                  fieldDef={fieldValue}
                  sjDb={sjDb}
                  isPdf
                />
              ),
            };
          },
        );

        return {
          title: groups[index].title,
          fields,
        };
      });

      //#region Legalized Copies
      if (personalId.legalizedCopy?.length) {
        sections.push({
          title: 'Legalized copies',
          itemGroups: [
            {
              items: personalId.legalizedCopy.map((item) => {
                const legalizationType = LegalizationTypeList.find(
                  (value) => value.key === item.legalizationType,
                );
                const legalizationTypeValue = legalizationType ? tf(legalizationType.label) : '-';
                const fileInfo = item.copyScan ? findFileMetaByPath(item.copyScan[0]) : null;

                return {
                  fields: [
                    {
                      label: 'Legalization type',
                      value: legalizationTypeValue,
                    },
                    {
                      label: 'Language of copy',
                      value:
                        !item.languageOfCopy || item.languageOfCopy === EMPTY_TEXT
                          ? EMPTY_TEXT
                          : getLanguageName(item.languageOfCopy),
                    },
                    {
                      label: 'Number of copies',
                      value: item.numberOfCopies ?? EMPTY_TEXT,
                    },
                    {
                      label: 'Storage location',
                      value:
                        item.copyStorageLocation ?? item.otherCopyStorageLocation ?? EMPTY_TEXT,
                    },
                    {
                      label: 'File',
                      value: (fileInfo?.name || fileInfo?.filePath) ?? EMPTY_TEXT,
                    },
                  ],
                };
              }),
            },
          ],
        });
      }

      //#region ID scans
      if (personalId.attachments?.length) {
        const uploadsData = personalId.attachments
          .map((filePath) => findFileMetaByPath(filePath))
          .filter(Boolean) as FileMetaInfoDto[];

        const inboxItems =
          inboxList?.items.filter((item) => personalId.emails?.includes(item.id)) ?? [];

        sections.push({
          title: 'ID scans',
          itemGroups: [
            {
              title: 'Uploads',
              count: uploadsData.length,
              items: uploadsData.map((upload) => ({
                fields: [
                  {
                    label: 'Name',
                    value: upload.name,
                  },
                  {
                    label: 'Upload date & time',
                    value: upload.createdAt ? dates.formatDateTime(upload.createdAt) : EMPTY_TEXT,
                  },
                  {
                    label: 'Comment',
                    value: upload.comment ?? EMPTY_TEXT,
                  },
                ],
              })),
            },
            {
              title: 'Email',
              count: inboxItems?.length ?? 0,
              items: inboxItems.map((upload) => ({
                fields: [
                  {
                    label: 'Name',
                    value: upload.attachments[0].name ?? EMPTY_TEXT,
                  },
                  {
                    label: 'Upload date & time',
                    value: upload.createdAt ? dates.formatDateTime(upload.createdAt) : EMPTY_TEXT,
                  },
                  {
                    label: 'Comment',
                    value: upload.attachments[0].comment ?? EMPTY_TEXT,
                  },
                ],
              })),
            },
          ],
        });
      }

      //#region Linked Entities
      const linkedAssets = getLinkedAssets(personalId);
      const linkedPrivateDocuments = getLinkedPrivateDocuments(personalId);

      if (linkedAssets.length || linkedPrivateDocuments.length) {
        sections.push({
          title: 'Linked entities',
          itemGroups: [
            {
              title: 'Personal Documents',
              count: linkedPrivateDocuments.length,
              items: linkedPrivateDocuments.map((personalDoc) => ({
                fields: [
                  {
                    label: 'Title',
                    value: personalDoc.name,
                  },
                  {
                    label: 'End date',
                    value: personalDoc.stopDate
                      ? dates.formatDate2(personalDoc.stopDate)
                      : EMPTY_TEXT,
                  },
                  {
                    label: 'Category',
                    value: personalDoc.privateCategory
                      ? privateDocValues.category(personalDoc.privateCategory)
                      : EMPTY_TEXT,
                  },
                  {
                    label: 'Country',
                    value: getCountryName(personalDoc.country),
                  },
                ],
              })),
            },
            {
              title: 'Assets',
              count: linkedAssets.length,
              items: linkedAssets.map((asset) => ({
                fields: [
                  {
                    label: 'Title',
                    value: asset[NAME_KEY_BY_ASSET_CATEGORY[asset.category]] as string,
                  },
                  {
                    label: 'Category',
                    value: assetValues.category(asset.category),
                  },
                  {
                    label: 'Country',
                    value: getCountryName(asset.country),
                  },
                ],
              })),
            },
          ],
        });
      }

      return { sections };
    };

    return { getEntityData };
  }, [t]);
};
